import React, { useCallback, useEffect, useRef } from 'react';
import type { Subscription } from 'rxjs';
import classnames from 'clsx';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons/faCircleNotch';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import {
  deleteShipmentDocumentRequest,
  deleteShipmentDocumentSuccess,
} from '../../../../../store/customs-documents/customs-documents.actions';
import { downloadDocument } from '../../../../../store/customs-documents/customs-documents.api';
import { getDocumentTypeLabel } from './document-type-dropdown';
import { messages } from './messages';
import { BLOB_URL_TTL_MS, ERROR_STATUSES } from './types';
import type { CustomsDocument } from './types';
import style from './customs-documents-section.module.less';

type DocumentListItemProps = {
  shipmentId: string;
  document: CustomsDocument;
  isSending?: boolean;
  onDismiss?: () => void;
};

/**
 * Single document row for shipment-level (post-ship) customs documents.
 *
 * Visual states (per the Figma screenshots):
 * - **initialized**: file link + doc type + trash icon  (status=uploaded, isSending=false)
 * - **sending**:     file link + doc type + "Sending..." spinner (isSending=true)
 * - **done**:        file link + doc type + "Submitted to carrier" green text (status=sent|queued)
 * - **error**:       red border + error text below file name (status=failed|invalid)
 */
export const DocumentListItem = ({ shipmentId, document, isSending, onDismiss }: DocumentListItemProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { documentId, fileName, documentType, status, error } = document;
  const isFileUploading = status === 'uploading';
  const isError = ERROR_STATUSES.includes(status);

  const isLocalOnly = documentId?.startsWith('failed-');

  const handleDelete = useCallback(() => {
    if (isLocalOnly) {
      dispatch(deleteShipmentDocumentSuccess(shipmentId, documentId));
    } else {
      dispatch(deleteShipmentDocumentRequest(shipmentId, documentId));
    }
  }, [dispatch, shipmentId, documentId, isLocalOnly]);

  const downloadSubRef = useRef<Subscription | null>(null);
  const blobCleanupRef = useRef<Array<{ url: string; timerId: ReturnType<typeof setTimeout> }>>([]);

  useEffect(
    () => () => {
      downloadSubRef.current?.unsubscribe();
      blobCleanupRef.current.forEach(({ url, timerId }) => {
        clearTimeout(timerId);
        URL.revokeObjectURL(url);
      });
    },
    [],
  );

  const handlePreview = useCallback(() => {
    downloadSubRef.current?.unsubscribe();
    downloadSubRef.current = downloadDocument(documentId).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      const timerId = setTimeout(() => {
        URL.revokeObjectURL(url);
        blobCleanupRef.current = blobCleanupRef.current.filter((entry) => entry.url !== url);
      }, BLOB_URL_TTL_MS);
      blobCleanupRef.current.push({ url, timerId });
    });
  }, [documentId]);

  const deleteButton = (onClick: () => void) => (
    <Button
      ariaLabel={intl.formatMessage(messages.deleteAriaLabel, { fileName })}
      className={style['delete-button']}
      onClick={onClick}
      theme={THEME_CONSTANTS.unstyled}
    >
      <FontAwesomeIcon icon={faTrashCan} />
    </Button>
  );

  // Determine which trailing element to show
  const renderTrailing = () => {
    if (isError) return onDismiss ? deleteButton(onDismiss) : null;
    if (status === 'queued') {
      return <Text className={style['status-submitted']} textType={TextType.Caption} value={messages.statusPendingSubmission} />;
    }
    if (status === 'sent') {
      return <Text className={style['status-submitted']} textType={TextType.Caption} value={messages.statusSubmittedToCarrier} />;
    }
    if (isSending || isFileUploading) {
      return (
        <FlexRow className={style['status-sending']} gap={Gap.Space4}>
          <FontAwesomeIcon icon={faCircleNotch} spin />
          <Text textType={TextType.Caption} value={messages.statusSending} />
        </FlexRow>
      );
    }
    return deleteButton(handleDelete);
  };

  const itemClassName = classnames(style['document-list-item'], {
    [style['document-list-item--error']]: isError,
  });

  return (
    <>
      <FlexRow className={itemClassName} gap={Gap.Space8}>
        <FlexColumn className={style['document-info']}>
          <Button
            ariaLabel={intl.formatMessage(messages.previewAriaLabel, { fileName })}
            onClick={handlePreview}
            theme={THEME_CONSTANTS.link}
          >
            {fileName}
          </Button>
          {documentType && (
            <Text className={style['document-type-label']} textType={TextType.Caption} value={getDocumentTypeLabel(intl, documentType)} />
          )}
        </FlexColumn>
        {renderTrailing()}
      </FlexRow>
      {isError && error && <Text className={style['error-message']} textType={TextType.Caption} value={error} />}
    </>
  );
};
